import { useMemo } from "react";
import { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import { Picker } from "@design-system/Picker/Picker.lib";
import type { PickerItemData } from "@design-system/Picker/Picker.types";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { HStack } from "@design-system/HStack/HStack";
import styles from "./ScheduleTemplateDrawer.module.scss";

interface Props {
    label: string;
    value: number | null;
    members: OrganizationMember[];
    getMemberLabel: (id: number | null) => string;
    onValueChange: (value: number | null) => void;
}

export const MemberCombobox = ({
    label,
    value,
    members,
    getMemberLabel,
    onValueChange,
}: Props) => {
    const placeholder = `Select ${label.toLowerCase()}...`;
    const memberMap = useMemo(
        () => new Map(members.map((m) => [m.user.id, m])),
        [members],
    );

    const items = useMemo<Array<PickerItemData<number>>>(
        () =>
            members.map((member) => ({
                value: member.user.id,
                label: getMemberLabel(member.user.id),
                leading: (
                    <UserAvatar user={member.user} size="xs" aria-hidden />
                ),
            })),
        [getMemberLabel, members],
    );

    return (
        <div>
            <label className={styles.fieldLabel}>{label}</label>
            <Picker.Select
                searchable
                items={items}
                value={value}
                onValueChange={onValueChange}
                placeholder={placeholder}
                searchPlaceholder={`Search ${label.toLowerCase()}s...`}
                emptyLabel="No matching members"
                renderValue={([id]) => {
                    const member = memberMap.get(id);
                    return member ? (
                        <HStack gap={8} align="center">
                            <UserAvatar
                                user={member.user}
                                size="sm"
                                aria-hidden
                            />
                            {getMemberLabel(member.user.id)}
                        </HStack>
                    ) : (
                        placeholder
                    );
                }}
                trigger={<Picker.Button variant="primary" size="xl" block />}
            />
        </div>
    );
};
